import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";

//COMPONENTS
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import useAsyncModalConfirm from "@/main/components/shared/asyncModalConfirm/asyncModalConfirm";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import ResultNotFoundComponent from "@/main/components/shared/resultNotFound/resultNotFoundComponent";

//REDUX/UTILS
import { KOGAreaButtonsModal } from "@/main/components/shared/styles";
import { getDefaultCountryOptionDrop, getTranslateByKey, isEmpty, isNullUndefined } from "@/main/services/utils";

// STYLES/COMPONENTS LOCAL
import { procurementApi } from "@/main/api";
import {
	GetBulkChangeItemsByClaimRequest,
	ProcurementProcessStatusBulkRequest,
	ReasonFilterRequest,
} from "@/main/models/request";
import { IThreeDotsClaimReturnProps } from "@/main/screens/claim/searchClaim/gridSearchClaim/rowSearchClaim/collapseSearchClaim/headerCollapseSearchClaim/threeDotsClaim/options/threeDotsClaimReturn";
import { useSearchClaimContext } from "@/main/screens/claim/searchClaim/context";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { useCountryReducer } from "@/main/store/country";
import { getReason } from "@/main/store/reason";

import SupplierBulkReturnClaim from "./supplierBulkReturnClaim/supplierBulkReturnClaim";
import { ISupplierReturn } from "./types";
import {
	backendToFrontendReturnClaim,
	getAllPartsForm,
	getAllPartsFormWithSupplier,
} from "./utilsmodalBulkReturnClaim";
import * as S from "./styles";
import { convertImageToBase64 } from "@/main/services/utilsFile";

export interface ImageInfo {
	id: string;
	files: File[];
}
export const INITIAL_STATE_FORM = {
	suppliers: [] as ISupplierReturn[],
};

export type IFormValues = typeof INITIAL_STATE_FORM;

interface IModalBulkReturnClaimProps extends IThreeDotsClaimReturnProps {
	isOpenModal: boolean;
	setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
	closeModal: () => void;
}

function ModalBulkReturnClaim({ isOpenModal, guidClaim, refreshClaim, closeModal }: IModalBulkReturnClaimProps) {
	useSearchClaimContext();
	const dispatch = useDispatch();
	const { countriesUser: countryReducer } = useCountryReducer();
	const [getConfirmation, ModalConfirmation] = useAsyncModalConfirm();

	const [isLoadingDataParts, setIsLoadingDataParts] = useState<boolean>(false);
	const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
	const [imagesGeneral, setImagesGeneral] = useState<ImageInfo[]>([]);

	const methods = useForm<IFormValues>({
		mode: "all",
		defaultValues: INITIAL_STATE_FORM,
	});
	const { control, handleSubmit } = methods;

	const watcherOptions = useWatch({
		control,
		name: "suppliers",
	});

	const { fields, replace } = useFieldArray({
		control,
		name: "suppliers",
	});
	const controlledFields = fields.map((field, index) => ({
		...field,
		...watcherOptions[index],
	}));

	function loadPartsByClaim() {
		setIsLoadingDataParts(true);
		const request: GetBulkChangeItemsByClaimRequest = {
			guidClaim,
			requestedStatus: 14,
		};

		procurementApi
			.getBulkChangeItemsByClaim(request)
			.then((response) => {
				const {
					data: { result },
				} = response;
				const optionsSelected = backendToFrontendReturnClaim(result);

				replace(optionsSelected);
			})
			.finally(() => {
				setIsLoadingDataParts(false);
			});
	}
	function validateForm(data: IFormValues) {
		const allParts = getAllPartsForm(data);
		const hasOneSelected = allParts.some((i) => i.isSelected);

		if (!hasOneSelected) {
			const message = getTranslateByKey("information.selectOne", true).replace(
				"{0}",
				getTranslateByKey("information.part").toLowerCase(),
			);
			toast.error(message);
			return false;
		}

		return true;
	}

	async function onSubmit(data: IFormValues) {
		if (!validateForm(data)) return;

		const isConfirmModal = await getConfirmation(getTranslateByKey("information.areYouSureYouWantToReturnTheParts"));

		if (!isConfirmModal) return;
		const request: ProcurementProcessStatusBulkRequest[] = [];
		setIsLoadingSave(true);
		const allPartsSelected = getAllPartsFormWithSupplier(data);
		for (const part of allPartsSelected) {
			const imageInfo = imagesGeneral.find((imageInfo) => imageInfo.id === part.guidProcurementSupplierItem);

			const imagesRequest = [];
			if (imageInfo) {
				for (const file of imageInfo.files) {
					const base64Image = await convertImageToBase64(file);
					imagesRequest.push({
						imageName: base64Image.name,
						imageUpload: `data:image/jpeg;base64,${base64Image.base64Image}`,
					});
				}
			}

			request.push({
				confirm: false,
				guidReason: isNullUndefined(part.supplier.reason) ? "" : String(part.supplier.reason.key),
				idReason: Number(part.supplier.reason?.enum),
				reasonDescription: part.supplier.reasonDescription,
				deliveryBySupplier: part.deliveryBySupplier,
				guidProcurement: part.guidProcurement,
				guidProcurementSupplier: part.guidProcurementSupplier,
				imagesRequest: imagesRequest,
				procurementSupplierItems: [
					{
						guidProcurementSupplierItem: part.guidProcurementSupplierItem,
					},
				],
			});
		}

		procurementApi
			.returnBulkItems(request)
			.then((responseApi) => {
				if (responseApi && responseApi.data && responseApi.data.result) {
					toast.success(getTranslateByKey("information.partsReturnedSuccessfully"));
					refreshClaim();
					closeModal();
				}
			})
			.finally(() => {
				setIsLoadingSave(false);
			});
	}

	function loadOptionsReason() {
		const countryUser = getDefaultCountryOptionDrop(countryReducer.data);
		if (isNullUndefined(countryUser)) return;

		const requestReason: ReasonFilterRequest = {
			guidCountry: String(countryUser.key),
			idReasonType: ReasonTypeEnum.Cancel,
		};
		dispatch(getReason(requestReason));
	}

	useEffect(() => {
		if (isOpenModal) {
			loadPartsByClaim();
			loadOptionsReason();
		}
	}, [isOpenModal]);

	if (isLoadingDataParts) return <LoadingComponent />;
	if (isEmpty(controlledFields)) return <ResultNotFoundComponent />;

	return (
		<form onSubmit={handleSubmit(onSubmit)} noValidate>
			<FormProvider {...methods}>
				<S.Container>
					{controlledFields.map((field, indexSupplier) => (
						<SupplierBulkReturnClaim
							key={field.id}
							indexSupplier={indexSupplier}
							supplier={field}
							setImagesGeneral={setImagesGeneral}
							imagesGeneral={imagesGeneral}
						/>
					))}
					<KOGAreaButtonsModal>
						<SecondaryButton id="id-searchClaim-btn-save-return" type="submit" isLoading={isLoadingSave}>
							{getTranslateByKey("information.save")}
						</SecondaryButton>
					</KOGAreaButtonsModal>
					<ModalConfirmation />
				</S.Container>
			</FormProvider>
		</form>
	);
}

function WrapperModalBulkReturnClaim(props: IModalBulkReturnClaimProps) {
	return (
		<KogModalSideComponent
			open={props.isOpenModal}
			shouldRenderOpen
			title={getTranslateByKey("information.returnParts")}
			closeModal={() => props.setIsOpenModal(false)}
		>
			<ModalBulkReturnClaim {...props} />
		</KogModalSideComponent>
	);
}

export default WrapperModalBulkReturnClaim;
